import { useCallback, useEffect, useMemo, useState } from 'react';
import { ReportFile } from 'types/report';
import { usePaging } from 'hooks/usePaging';
import { SIZE_PAGE_REPORT } from 'const/paging';
import { deleteFileReport, getReportFile, sendReport } from 'services/report';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { findIndex, fromPairs } from 'lodash';
import { useFlash } from 'contexts/flash';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';
import { SearchType } from 'types/loctions';

export const useInjection = () => {
  const { setFlash } = useFlash();
  const { users } = useFilteredRoomContext();
  const [search, setSearch] = useState<string>('');
  const [searchType, setSearchType] = useState<SearchType>('partial-text');
  const [filter, setFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('');
  const [order, setOrder] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [, setSearchText] = useState<string>('');
  const [dataReportTable, setDataReportTable] = useState<ReportFile[]>([]);
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);
  const [dataSendMail, setDataSendMail] = useState<ReportFile>();
  // table report START
  const { pagingInfo, setCurrentPage, setTotal } = usePaging({
    rowsPerPage: SIZE_PAGE_REPORT
  });
  const [filterValidations, setFilterValidations] = useState<
    {
      key: string;
      value: string;
      label: string;
      level?: number;
    }[]
  >();
  const params = useMemo(() => {
    const filterParams = fromPairs(
      (filterValidations ?? [])
        .filter((item) => item.key && item.value)
        .map((item) => [item.key, item.value])
    );
    return {
      size: SIZE_PAGE_REPORT,
      page: page,
      keyword: search || undefined,
      searchType,
      sortBy: sortBy || undefined,
      order: order || undefined,
      ...filterParams
    };
  }, [order, page, search, searchType, sortBy, filterValidations]);

  const { systemDropdownValues: options } = useFilteredRoomContext();

  const getDataTableReport = useCallback(async () => {
    const data = await getReportFile(params);
    setDataReportTable(data?.reportFiles);
    setTotal(data.total);
  }, [params, setTotal]);

  const { asyncCallback: asyncGetDataTableReport } = useAsyncCallback(getDataTableReport, []);
  useEffect(() => {
    asyncGetDataTableReport();
  }, [params, asyncGetDataTableReport]);

  const onSearch = useCallback(() => {
    setSearchText(search);
    setPage(1);
    setCurrentPage(1);
  }, [search, setCurrentPage]);

  const sortOption = useMemo(
    () => ({
      field: sortBy,
      order: order
    }),
    [sortBy, order]
  );

  const handleSort = useCallback(
    (orderBy: string) => {
      if (orderBy === sortBy) {
        setOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(orderBy);
        setOrder('asc');
      }
    },
    [sortBy]
  );

  const handleChangePage = useCallback(
    (value: number) => {
      setCurrentPage(value);
      setIsSelectAll(false);
      setPage(value);
    },
    [setCurrentPage]
  );

  const handleFilterValidation = useCallback(
    async (value?: { key: string; value: string; label: string; level?: number }) => {
      if (!value) return;

      setFilterValidations((prevState) => {
        let nextState: typeof prevState;

        if (!prevState) {
          nextState = [value];
        } else {
          const index = findIndex(prevState, { key: value.key });
          if (index !== -1) {
            const existingItem = prevState[index];
            if (
              (!value?.level && existingItem.value === value.value) ||
              Number(value?.level) === Number(existingItem?.level)
            ) {
              nextState = [...prevState.slice(0, index), ...prevState.slice(index + 1)];
            } else {
              const updated = [...prevState];
              updated[index] = value;
              nextState = updated;
            }
          } else {
            nextState = [...prevState, value];
          }
        }
        return nextState;
      });
    },
    []
  );

  const handleSendMail = useCallback(
    async (mails: { email: string[] }) => {
      if (!dataSendMail?.id) return;
      setDataSendMail(undefined);
      await sendReport(dataSendMail?.id, { emails: mails.email });
      setFlash({
        type: 'success',
        message: 'Sent successfully!'
      });
    },
    [dataSendMail?.id, setFlash]
  );

  const { asyncCallback: asyncHandleSendMail } = useAsyncCallback(handleSendMail, [handleSendMail]);

  const handleDeleteReport = useCallback(
    async (id: number) => {
      if (!confirm('Are you sure you want to delete this report?')) return;
      await deleteFileReport(id);
      setDataReportTable((prevState) => prevState.filter((item) => item.id !== id));
      setFlash({
        type: 'success',
        message: 'Delete successfully!'
      });
    },
    [setFlash]
  );

  const { asyncCallback: asyncHandleDeleteReport } = useAsyncCallback(handleDeleteReport, [
    handleDeleteReport
  ]);

  return {
    search,
    setSearch,
    searchType,
    setSearchType,
    filter,
    setFilter,
    pagingInfo,
    onSearch,
    sortOption,
    handleSort,
    dataReportTable,
    handleChangePage,
    checkedIds,
    setCheckedIds,
    isSelectAll,
    setIsSelectAll,
    setFilterValidations: handleFilterValidation,
    filterValidations,
    dataSendMail,
    setDataSendMail,
    users,
    asyncHandleSendMail,
    options,
    onUpdatePagination: (page: number) => {
      setCurrentPage(page);
      setPage(page);
    },
    asyncHandleDeleteReport
  };
};
